import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { ClipboardCopy, ExternalLink, Loader2, PanelLeft, PanelRight, Search } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { hubModalOverlayClassName, hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import {
  FLOOR_PAGE_SCAN_TARGET_PROPS,
  useFloorPageScanCapture,
} from "@/lib/floorPageScanCapture";
import { cn } from "@/lib/utils";
import { EM_DASH } from "@/lib/utils";

import {
  DEVICE_LOOKUP_QUERY_KEY,
  fetchDeviceLookupReport,
  type DeviceLookupReport,
} from "./deviceLookupApi";

const MODAL_HELP =
  "Look up a device by label serial, PCBA serial, IMEI, ICCID, MCU UUID, or box serial. " +
  "Use pcba:, @pcba, imei:, @imei, and similar prefixes to search one field only. " +
  "Open the full report for potting, labor scans, and quality history.";

const DEVICE_LOOKUP_PANEL_SIDE_STORAGE_KEY = "hub.configuration.deviceLookupPanelSide";

type DeviceLookupPanelSide = "left" | "right";

export type DeviceLookupApplySerialsInput = {
  pcbaSerial: string | null;
  clampSerial: string | null;
};

function readStoredPanelSide(): DeviceLookupPanelSide {
  try {
    const stored = sessionStorage.getItem(DEVICE_LOOKUP_PANEL_SIDE_STORAGE_KEY);
    return stored === "left" ? "left" : "right";
  } catch {
    return "right";
  }
}

function deviceLookupFullPagePath(searchTerm: string): string {
  return `/manufacturing/device-lookup?serial=${encodeURIComponent(searchTerm.trim())}`;
}

function MiniDetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  const display = value == null || value.trim() === "" ? EM_DASH : value;
  return (
    <div className="grid gap-0.5 sm:grid-cols-[minmax(0,7rem)_minmax(0,1fr)] sm:gap-3">
      <dt className="text-xs font-medium text-slate-600">{label}</dt>
      <dd className="break-all font-mono text-xs text-slate-900">{display}</dd>
    </div>
  );
}

function DeviceLookupMiniReport({
  report,
  searchTerm,
  onApplyToConfiguration,
}: {
  report: DeviceLookupReport;
  searchTerm: string;
  onApplyToConfiguration?: (input: DeviceLookupApplySerialsInput) => void;
}) {
  const { serialFci, manufacturingState } = report;
  const pcbaSerial = serialFci.pcbaSerial?.trim() ?? "";
  const clampSerial = serialFci.clampSerial?.trim() ?? "";
  const canCopyToConfiguration =
    onApplyToConfiguration != null && (pcbaSerial !== "" || clampSerial !== "");

  return (
    <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="neutral">{manufacturingState.headline}</Badge>
        {serialFci.offBatchItemLabel ? <Badge tone="muted">{serialFci.offBatchItemLabel}</Badge> : null}
      </div>
      <p className="text-sm text-slate-700">{manufacturingState.detail}</p>
      <dl className="space-y-2">
        <MiniDetailRow label="Label serial" value={serialFci.deviceLabelSerial} />
        <MiniDetailRow label="PCBA serial" value={serialFci.pcbaSerial} />
        <MiniDetailRow label="Finished SKU" value={serialFci.finishedSku} />
        <MiniDetailRow label="IMEI / ICCID" value={`${serialFci.imei ?? EM_DASH} / ${serialFci.iccid ?? EM_DASH}`} />
        <MiniDetailRow label="MCU UUID" value={serialFci.mcuUuid} />
        <MiniDetailRow label="Clamp serial" value={serialFci.clampSerial} />
        <MiniDetailRow label="Box serial" value={serialFci.boxSerial} />
        <MiniDetailRow label="serial_fci" value={String(serialFci.id)} />
      </dl>
      {canCopyToConfiguration ? (
        <Button
          type="button"
          variant="secondary"
          className="w-full"
          onClick={() =>
            onApplyToConfiguration?.({
              pcbaSerial: pcbaSerial === "" ? null : pcbaSerial,
              clampSerial: clampSerial === "" ? null : clampSerial,
            })
          }
        >
          <ClipboardCopy className="mr-1.5 h-4 w-4 shrink-0" aria-hidden />
          Copy PCBA and clamp to configuration
        </Button>
      ) : null}
      <Link
        to={deviceLookupFullPagePath(searchTerm)}
        className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 underline decoration-blue-700/40 underline-offset-2 hover:text-blue-900"
      >
        Open full device lookup
        <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </Link>
    </div>
  );
}

export function DeviceLookupModal({
  open,
  onClose,
  onApplyToConfiguration,
}: {
  open: boolean;
  onClose: () => void;
  onApplyToConfiguration?: (input: DeviceLookupApplySerialsInput) => void;
}): JSX.Element | null {
  const [input, setInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [panelSide, setPanelSide] = useState<DeviceLookupPanelSide>(() => readStoredPanelSide());
  const serialInputRef = useRef<HTMLInputElement>(null);

  useFloorPageScanCapture({
    enabled: open,
    inputRef: serialInputRef,
    onScan: (serial) => {
      setInput(serial);
      setSearchTerm(serial);
    },
  });

  useEffect(() => {
    if (!open) {
      setInput("");
      setSearchTerm("");
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const lookupQuery = useQuery({
    queryKey: [DEVICE_LOOKUP_QUERY_KEY, "modal", searchTerm],
    queryFn: () => fetchDeviceLookupReport(searchTerm),
    enabled: open && searchTerm.trim() !== "",
    retry: false,
  });

  if (!open) {
    return null;
  }

  const errorMessage =
    searchTerm !== "" && lookupQuery.isError
      ? lookupQuery.error instanceof ApiError && lookupQuery.error.status === 404
        ? `No device found for ${searchTerm}.`
        : lookupQuery.error instanceof Error
          ? lookupQuery.error.message
          : "Device lookup failed."
      : null;

  function togglePanelSide(): void {
    setPanelSide((current) => {
      const next = current === "right" ? "left" : "right";
      try {
        sessionStorage.setItem(DEVICE_LOOKUP_PANEL_SIDE_STORAGE_KEY, next);
      } catch {
        /* private mode */
      }
      return next;
    });
  }

  return createPortal(
    <div
      {...hubModalOverlayProps(false)}
      className={cn(hubModalOverlayClassName(false), "p-0")}
      role="dialog"
      aria-modal="true"
      aria-labelledby="device-lookup-modal-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={cn(
          "absolute inset-y-0 flex w-full max-w-lg flex-col border-slate-200 bg-white shadow-xl",
          panelSide === "right" ? "right-0 border-l" : "left-0 border-r",
        )}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="border-b border-slate-200 px-4 py-3 sm:px-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <h2 id="device-lookup-modal-title" className="text-lg font-semibold text-slate-900">
                Device lookup
              </h2>
              <HoverHintWithGlyph hoverHint={MODAL_HELP} size="md" />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={togglePanelSide}
              aria-label={panelSide === "right" ? "Move device lookup panel to the left" : "Move device lookup panel to the right"}
            >
              {panelSide === "right" ? (
                <PanelLeft className="mr-1.5 h-4 w-4 shrink-0" aria-hidden />
              ) : (
                <PanelRight className="mr-1.5 h-4 w-4 shrink-0" aria-hidden />
              )}
              {panelSide === "right" ? "Move left" : "Move right"}
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5">
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              setSearchTerm(input.trim());
            }}
          >
            <div className="min-w-[12rem] flex-1 space-y-1">
              <Label htmlFor="configuration-device-lookup-input">Device identifier</Label>
              <Input
                id="configuration-device-lookup-input"
                ref={serialInputRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="pcba:SD-8211-2126-00242, @imei …"
                className="font-mono"
                autoComplete="off"
                autoFocus
                {...FLOOR_PAGE_SCAN_TARGET_PROPS}
              />
            </div>
            <Button type="submit" disabled={input.trim() === ""} isLoading={lookupQuery.isFetching}>
              <Search className="h-4 w-4" aria-hidden />
              Search
            </Button>
          </form>

          {lookupQuery.isFetching ? (
            <p className="text-sm text-slate-600">
              <Loader2 className="mr-1 inline h-4 w-4 animate-spin" aria-hidden />
              Loading device report…
            </p>
          ) : null}

          {errorMessage ? <p className="text-sm text-slate-600">{errorMessage}</p> : null}

          {lookupQuery.data ? (
            <DeviceLookupMiniReport
              report={lookupQuery.data}
              searchTerm={searchTerm}
              onApplyToConfiguration={onApplyToConfiguration}
            />
          ) : null}
        </div>

        <div className="flex justify-end border-t border-slate-200 px-4 py-3 sm:px-5">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function DeviceLookupModalTrigger({
  className,
  onApplyToConfiguration,
}: {
  className?: string;
  onApplyToConfiguration?: (input: DeviceLookupApplySerialsInput) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button type="button" variant="secondary" className={className} onClick={() => setOpen(true)}>
        <Search className="mr-1.5 h-4 w-4" aria-hidden />
        Device lookup
      </Button>
      <DeviceLookupModal
        open={open}
        onClose={() => setOpen(false)}
        onApplyToConfiguration={onApplyToConfiguration}
      />
    </>
  );
}
